import { useEffect, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

// Loads the current user's capability keys for the account via Codex's
// public.current_account_permissions(target_account_id) -> table(permission_key, source).
// The console gates nav + sensitive actions on these keys instead of hard-coding role arrays.
// Fails CLOSED: any error / unexpected shape yields an empty permission set.

export interface PermissionsState {
  loading: boolean;
  keys: ReadonlySet<string>;
  isSuperadmin: boolean;
  can: (permissionKey: string) => boolean;
}

interface PermissionRow {
  permission_key?: unknown;
}

/** Pull permission_key strings out of the rpc result rows (defensive). */
export function extractPermissionKeys(data: unknown): string[] {
  return [...extractPermissionScope(data).keys];
}

export function extractPermissionScope(data: unknown): { keys: Set<string>; isSuperadmin: boolean } {
  if (!Array.isArray(data)) {
    return { keys: new Set<string>(), isSuperadmin: false };
  }
  const keys = new Set<string>();
  let isSuperadmin = false;
  for (const row of data as PermissionRow[]) {
    if (row && typeof row.permission_key === "string" && row.permission_key.length > 0) {
      keys.add(row.permission_key);
      if ((row as { source?: unknown }).source === "platform_superadmin") {
        isSuperadmin = true;
      }
    }
  }
  return { keys, isSuperadmin };
}

/**
 * `refreshKey` forces a refetch when something outside the account id changes what the server
 * will answer — today that is a platform support session starting or ending, which rewrites this
 * caller's matrix for the SAME account (superadmin union out, target role matrix in). Pass any
 * value that changes on that transition; the hook re-reads whenever it differs.
 */
export function usePermissions(
  client: SupabaseClient,
  accountId: string,
  refreshKey: string | number | null = null,
): PermissionsState {
  const [keys, setKeys] = useState<ReadonlySet<string>>(() => new Set<string>());
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);

    async function loadPermissions(): Promise<void> {
      try {
        const { data, error } = await client.rpc("current_account_permissions", { target_account_id: accountId });
        if (!active) return;
        const scope = error !== null ? { keys: new Set<string>(), isSuperadmin: false } : extractPermissionScope(data);
        setKeys(scope.keys);
        setIsSuperadmin(scope.isSuperadmin);
        setLoading(false);
      } catch {
        if (!active) return;
        setKeys(new Set<string>());
        setIsSuperadmin(false);
        setLoading(false);
      }
    }

    void loadPermissions();

    return () => {
      active = false;
    };
  }, [client, accountId, refreshKey]);

  return { loading, keys, isSuperadmin, can: (permissionKey: string) => keys.has(permissionKey) };
}
